class ModelPicker extends HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.section;
		this.location = this.dataset.location || '';

		this.dialog = this.querySelector('[data-model-dialog]');
		this.triggerBtn = this.querySelector('[data-model-open]');
		this.closeBtn = this.querySelector('[data-model-close]');
		this.doneBtn = this.querySelector('[data-model-done]');
		this.cards = Array.from(this.querySelectorAll('[data-model-card]'));
		this.footerSwatch = this.querySelector('[data-model-footer-swatch]');
		this.footerName = this.querySelector('[data-model-footer-name]');
		this.footerPrice = this.querySelector('[data-model-footer-price]');
		this.dialogImages = Array.from(this.querySelectorAll('[data-model-card] img'));

		this.pendingCard = this.cards.find((c) => c.classList.contains('is-selected')) || null;

		this.triggerBtn?.addEventListener('click', () => this.open());
		this.triggerBtn?.addEventListener('pointerenter', () => this.prepareImages(), { once: true });
		this.triggerBtn?.addEventListener('touchstart', () => this.prepareImages(), { once: true, passive: true });
		this.triggerBtn?.addEventListener('focus', () => this.prepareImages(), { once: true });
		this.closeBtn?.addEventListener('click', () => this.close());
		this.dialog?.addEventListener('cancel', (e) => {
			e.preventDefault();
			this.close();
		});
		this.dialog?.addEventListener('click', (e) => {
			if (e.target === this.dialog) this.close();
		});

		this.cards.forEach((card) => {
			card.addEventListener('click', () => this.pickCard(card));
		});

		this.doneBtn?.addEventListener('click', () => this.commit());
	}

	open({ instant = false } = {}) {
		if (!this.dialog) return;
		this.prepareImages();
		if (instant) this.dialog.classList.add('no-transition');
		if (this.dialog.showModal) {
			try {
				this.dialog.showModal();
			} catch (_) {
				this.dialog.setAttribute('open', '');
			}
		} else {
			this.dialog.setAttribute('open', '');
		}
		document.body.style.overflow = 'hidden';
		if (instant) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => this.dialog?.classList.remove('no-transition'));
			});
		}
	}

	close() {
		if (this.dialog?.close) this.dialog.close();
		else this.dialog?.removeAttribute('open');
		document.body.style.overflow = '';
	}

	prepareImages() {
		if (this.imagesPrepared) return;
		this.imagesPrepared = true;
		this.dialogImages.forEach((image, index) => {
			image.loading = 'eager';
			image.decoding = 'async';
			image.fetchPriority = index < 8 ? 'high' : 'low';
		});
	}

	pickCard(card) {
		this.cards.forEach((c) => c.classList.remove('is-selected'));
		card.classList.add('is-selected');
		this.pendingCard = card;
		this.updateFooter(card);

		const url = card.dataset.productUrl;
		if (!url || url === '#') return;
		const target = new URL(url, window.location.origin);
		if (target.pathname === window.location.pathname) return;
		const withParams = ModelPicker.preserveMaterialPickerParams(target.href);
		this.navigateTo(withParams, card);
	}

	updateFooter(card) {
		if (this.footerSwatch) {
			const url = card.dataset.image;
			this.footerSwatch.innerHTML = url ? `<img src="${url}" alt="">` : '';
		}
		if (this.footerName) this.footerName.textContent = card.dataset.name || '';
		if (this.footerPrice) this.footerPrice.textContent = card.dataset.price || '';
	}

	commit() {
		if (!this.pendingCard) {
			this.close();
			return;
		}
		const url = this.pendingCard.dataset.productUrl;
		if (!url || url === '#') {
			this.close();
			return;
		}
		const target = new URL(url, window.location.origin);
		if (target.pathname === window.location.pathname) {
			this.close();
			return;
		}
		const withParams = ModelPicker.preserveMaterialPickerParams(target.href);
		this.navigateTo(withParams, this.pendingCard);
	}

	static preserveMaterialPickerParams(href) {
		const url = new URL(href, window.location.origin);
		const src = new URLSearchParams(window.location.search);
		['mp_color', 'mp_fabric'].forEach((key) => {
			const val = src.get(key);
			if (val) url.searchParams.set(key, val);
		});
		return url.toString();
	}

	async navigateTo(href, card, options = {}) {
		if (ModelPicker._navigating) return;
		ModelPicker._navigating = true;
		card?.classList.add('is-loading');
		this.classList.add('is-navigating');
		ModelPicker.showLoading();
		const pushHistory = options.pushHistory !== false;

		const oldWrapper = document.getElementById(`shopify-section-${this.sectionId}`);
		const wantedLocation = this.location;
		const wasOpen = !!this.dialog?.open;

		try {
			const requestUrl = new URL(href, window.location.origin);
			if (this.sectionId) requestUrl.searchParams.set('section_id', this.sectionId);
			const res = await fetch(requestUrl, { headers: { Accept: 'text/html' } });
			if (!res.ok) throw new Error(`page ${res.status}`);
			const html = await res.text();

			const doc = new DOMParser().parseFromString(html, 'text/html');

			const newWrapper =
				(this.sectionId && doc.getElementById(`shopify-section-${this.sectionId}`)) ||
				doc.querySelector('model-picker')?.closest('.shopify-section');

			if (!newWrapper) throw new Error('new section not found in target page');

			const targetWrapper = oldWrapper || document.querySelector('model-picker')?.closest('.shopify-section');
			if (!targetWrapper) throw new Error('current section wrapper missing');

			const modelTitle = doc.querySelector('model-picker[data-product-page-title]')?.dataset.productPageTitle?.trim();
            if (modelTitle || doc.title) document.title = modelTitle || doc.title;
			if (pushHistory) history.pushState({ modelPicker: true }, '', href);

			targetWrapper.innerHTML = newWrapper.innerHTML;
			if (targetWrapper.id !== newWrapper.id) targetWrapper.id = newWrapper.id;
			ModelPicker.runScripts(targetWrapper);

			requestAnimationFrame(() => {
				const newPicker =
					targetWrapper.querySelector(
						`model-picker[data-location="${wantedLocation}"]`,
					) || targetWrapper.querySelector('model-picker');
				if (newPicker && wasOpen) newPicker.open({ instant: true });
				document.dispatchEvent(
					new CustomEvent('model-picker:navigated', { detail: { url: href } }),
				);
			});
		} catch (err) {
			console.error('[model-picker] navigation failed, falling back to full reload', err);
			window.location.href = href;
		} finally {
			ModelPicker._navigating = false;
			ModelPicker.hideLoading();
		}
	}

	static showLoading() {
		if (document.getElementById('model-picker-loading')) return;
		const overlay = document.createElement('div');
		overlay.id = 'model-picker-loading';
		overlay.className = 'model-picker-loading';
		overlay.innerHTML = '<div class="model-picker-loading__spinner" role="status" aria-label="Loading"></div>';
		document.body.appendChild(overlay);
	}

	static hideLoading() {
		document.getElementById('model-picker-loading')?.remove();
	}

	static runScripts(root) {
		root.querySelectorAll('script').forEach((oldScript) => {
			if (oldScript.src) return;
			const newScript = document.createElement('script');
			for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
			newScript.textContent = oldScript.textContent;
			oldScript.parentNode.replaceChild(newScript, oldScript);
		});
	}
}

if (!customElements.get('model-picker')) {
	customElements.define('model-picker', ModelPicker);
}

if (!window.__modelPickerPopstateBound) {
	window.__modelPickerPopstateBound = true;
	window.addEventListener('popstate', () => {
		const picker = document.querySelector('model-picker');
		if (!picker) {
			window.location.reload();
			return;
		}
		picker.navigateTo(window.location.href, null, { pushHistory: false }).catch(() => window.location.reload());
	});
}
